import { Activity } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import type { MetricDataPoint, ResourceMetricsSeries } from "@/types/admin";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_GRID_STROKE,
  CHART_TICK_FILL,
  CHART_GRID_STROKE_OPACITY,
  chartTooltipContentStyle,
} from "@/lib/chartTheme";
import { formatPercent, percentAxisMax } from "@/lib/percentAxis";

function formatMemoryMb(mb?: number | null): string {
  if (mb == null) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

function ChartEmptyState({ label }: { label?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 bg-muted/20 px-3 text-center">
      <span className="flex h-7 w-7 items-center justify-center rounded-full border border-border/80 bg-background">
        <Activity className="h-3.5 w-3.5 text-muted-foreground" />
      </span>
      <span className="text-[11px] leading-tight text-muted-foreground">
        {label ?? "No data yet"}
      </span>
    </div>
  );
}

interface VmOverviewChartsProps {
  metrics?: ResourceMetricsSeries;
  isLoading: boolean;
  memoryMb?: number | null;
  /**
   * Replaces the charts' "No data yet" when the caller knows why there is nothing to draw —
   * the VM is still provisioning, or it will never report. Undefined means the query ran and
   * simply came back empty.
   */
  emptyLabel?: string;
  /**
   * Set when the metrics request failed. With no samples to draw, the charts say so instead of
   * "No data yet", which read as a VM that had reported nothing.
   */
  error?: unknown;
}

/**
 * Mini CPU/Memory area charts for the Overview tab — the client app's VmOverviewCharts
 * (ResourceOverviewTab.tsx): same titles, headline (the newest point), axes and styling, always fed the
 * last-1h series regardless of whatever range the Metrics tab has selected.
 *
 * The one addition is "CPU usage (raw)", after the client's two charts: the host reading before the
 * VmCpuLimitRatio rescale. Only CPU is rescaled.
 */
export function VmOverviewCharts({
  metrics,
  isLoading,
  memoryMb,
  emptyLabel: callerEmptyLabel,
  error,
}: VmOverviewChartsProps) {
  const emptyLabel = error ? "Could not load metrics" : callerEmptyLabel;

  const toChartData = (series?: MetricDataPoint[]) =>
    series?.map((point) => ({
      time: format(new Date(point.timestamp), "HH:mm"),
      value: point.value,
    })) ?? [];

  const cpuData = toChartData(metrics?.cores);
  const hostCpuData = toChartData(metrics?.hostCores);
  const memoryData = toChartData(metrics?.memory);

  const last = (data: { value: number }[]) =>
    data.length ? data[data.length - 1]!.value : null;

  const lastMem = last(memoryData);
  const totalMemGb = memoryMb != null ? memoryMb / 1024 : null;
  const approxUsedGb =
    lastMem != null && totalMemGb != null
      ? ((lastMem / 100) * totalMemGb).toFixed(1)
      : null;

  const chartWrap =
    "min-w-0 rounded-xl border border-border/80 bg-card p-4 shadow-none";

  const miniChart = (
    title: string,
    subtitle: string,
    data: { time: string; value: number }[],
    gradientId: string,
    stroke: string,
    subline: string | null,
    tooltipLabel: string,
    axisMax: number | ((dataMax: number) => number),
  ) => {
    const headline = last(data);

    return (
      <div className={chartWrap}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium">{title}</div>
            <div className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              {subtitle}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold tabular-nums leading-none tracking-tight">
              {/* One decimal, not a whole number: the rescaled and raw CPU headlines sit side by side,
                  and rounding each on its own made 8.4% raw read as "8%" next to "12%" under a 0.7
                  ratio, which looks like the wrong factor. */}
              {headline != null
                ? `${headline.toFixed(1)}%`
                : isLoading
                  ? "…"
                  : "—"}
            </div>
            {subline && (
              <div className="mt-1 text-[10px] text-muted-foreground">
                {subline}
              </div>
            )}
          </div>
        </div>
        <div className="h-[120px] w-full">
          {isLoading && !data.length ? (
            <Skeleton className="h-full w-full rounded-lg" />
          ) : !data.length ? (
            <ChartEmptyState label={emptyLabel} />
          ) : (
            // A fixed height, as on the client: recharts evaluates its size warning before the
            // ResizeObserver has measured anything, so "100%" logs a -1 width/height on every mount.
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart
                data={data}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={stroke} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: CHART_TICK_FILL }}
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                />
                <YAxis
                  domain={[0, axisMax]}
                  width={34}
                  tick={{ fontSize: 10, fill: CHART_TICK_FILL }}
                  tickCount={3}
                  tickFormatter={(v: number) => `${v}%`}
                  stroke={CHART_GRID_STROKE}
                  strokeOpacity={CHART_GRID_STROKE_OPACITY}
                />
                <Tooltip
                  contentStyle={chartTooltipContentStyle}
                  formatter={(value) => [formatPercent(value), tooltipLabel]}
                />
                {/* No animation: each poll slides the window one sample, and the default 1.5s tween moved
                    every point to its neighbour's value, so the whole line rippled once a minute. */}
                <Area
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="value"
                  stroke={stroke}
                  fillOpacity={1}
                  fill={`url(#${gradientId})`}
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-2">
      {miniChart(
        "CPU usage",
        "Last hour · % busy",
        cpuData,
        "adminOvCpu",
        "var(--info)",
        "Recent window",
        "CPU",
        // Scales to the reading, so a VM idling under 1% still draws a visible line.
        percentAxisMax,
      )}
      {miniChart(
        "Memory",
        `Used / total · ${formatMemoryMb(memoryMb)}`,
        memoryData,
        "adminOvMem",
        "var(--brand)",
        approxUsedGb ? `≈ ${approxUsedGb} GB used` : null,
        "RAM",
        100,
      )}
      {/* Admin only: the CPU series before the VmCpuLimitRatio rescale. */}
      {miniChart(
        "CPU usage (raw)",
        "Last hour · as measured",
        hostCpuData,
        "adminOvHostCpu",
        "var(--info)",
        "Recent window",
        "CPU (raw)",
        percentAxisMax,
      )}
    </div>
  );
}
